/** Preekie Meetings' screens (approved mockups "Preekie Meetings" revision
 * 3): the Meetings page, the New meeting dialog, the guest lobby, the
 * meeting room, in-person mode and the summary review. Plain DOM, like
 * render.ts. Words live in meetings.ts (MEETINGS_COPY) so they're tested. */
import {
  ApiError,
  admitGuest,
  approveSummary,
  editSummary,
  endMeeting,
  enterGuestLobby,
  fetchGuestLobby,
  fetchHandoverOptions,
  fetchLobby,
  fetchMeeting,
  fetchSummary,
  fetchTranscript,
  guestJoin,
  joinMeeting,
  startRecording,
  stopRecording,
} from "./api.js";
import type {
  CreatedMeeting,
  ReviewItem,
  JoinInfo,
  GuestLobbyInfo,
  LobbyGuest,
  MeetingAction,
  MeetingDetail,
  MeetingRow,
  MeetingSummary,
  MeetingsList,
} from "./api.js";
import type { ChimeSession } from "./chime.js";
import { LocalMedia } from "./media.js";
import {
  AI_DRAFT_NOTICE,
  MAX_GUESTS,
  actionDue,
  figureSource,
  inferredDateLine,
  itemCheck,
  reviewCounts,
  MEETINGS_COPY as T,
  RECORDING_NOTICE,
  audioDeletedLine,
  attendeeName,
  canJoin,
  canReview as canReviewNotes,
  canRunMeeting,
  clockLabel,
  createPayload,
  formProblems,
  formatTableWhen,
  formatWhen,
  lengthLabel,
  mediaErrorMessage,
  parseEmails,
  recordingLabel,
  statusStyle,
  speakerLabel,
  pauseMarker,
  pausesLine,
} from "./meetings.js";
import type { NewMeetingForm } from "./meetings.js";
import { buildUrl } from "./nav.js";
import type { FilterOption, View } from "./nav.js";
import { el } from "./render.js";

const PRIVACY_POLICY_URL = "https://preekie.com/privacy.html";
const POLL_MS = 2500; // the API doc: poll the lobby every 2-3 seconds

function messageOf(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

function button(text: string, cls: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", cls, text);
  b.type = "button";
  b.addEventListener("click", onClick);
  return b;
}

function alertBox(message: string): HTMLElement {
  const box = el("div", "pk-alert", message);
  box.setAttribute("role", "alert");
  return box;
}

export function statusPill(status: string): HTMLElement {
  return el("span", `status pk-severity-${statusStyle(status)}`, status);
}

/** In-app link: a real href (opens in a new tab if asked), handled in place. */
function viewLink(text: string, view: View, clients: readonly string[], onNavigate: (v: View) => void): HTMLAnchorElement {
  const a = el("a", "pk-meeting-link", text);
  a.href = buildUrl(view, clients);
  a.addEventListener("click", (ev) => {
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
    ev.preventDefault();
    onNavigate(view);
  });
  return a;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? parts[0]![0]! + parts[parts.length - 1]![0]! : (parts[0] ?? "?").slice(0, 2)).toUpperCase();
}

// ---- The Meetings page ----

export interface MeetingsPageOptions {
  list: MeetingsList | null;
  error: string | null;
  timeZone: string;
  clients: readonly string[];
  onRecord: () => void; // the device recorder (R3)
  onStartInPerson?: () => void; // the in-person meeting room, kept working until the recorder's phone path replaces it; absent with Chime off
  onNavigate: (view: View) => void;
}

/** A meetings table (R1, R2): When, Meeting (with where it was), Client,
 * Length, Status. Every table shares one set of fixed column widths, so the
 * columns line up from one table to the next. The client page leaves out
 * the Client column. */
export function meetingTable(
  caption: string,
  rows: readonly MeetingRow[],
  empty: string,
  kind: "upcoming" | "review" | "approved",
  opts: { timeZone: string; clients: readonly string[]; onNavigate: (view: View) => void },
  showClient = true
): HTMLElement {
  const section = el("section", "pk-group");
  const id = `pk-meetings-${kind}`;
  section.setAttribute("aria-labelledby", id);
  const heading = el("h2", "pk-section-title pk-meetings-heading", caption);
  heading.id = id;
  section.append(heading);
  if (rows.length === 0) {
    section.append(el("div", "pk-empty", empty));
    return section;
  }
  const table = el("table", "pk-table pk-table-static pk-meetings-table");
  const colgroup = el("colgroup");
  for (const c of ["when", "meeting", ...(showClient ? ["client"] : []), "length", "status"]) colgroup.append(el("col", `pk-col-${c}`));
  const head = el("tr");
  head.append(el("th", undefined, "When"), el("th", undefined, "Meeting"));
  if (showClient) head.append(el("th", undefined, "Client"));
  head.append(el("th", "pk-num", "Length"), el("th", undefined, "Status"));
  const thead = el("thead");
  thead.append(head);
  const tbody = el("tbody");
  for (const row of rows) {
    const tr = el("tr");
    const when = el("td", "figure", formatTableWhen(row.scheduled_start, opts.timeZone));
    const meeting = el("td");
    const target: View =
      kind === "upcoming" ? { kind: "meeting-room", meetingId: row.meeting_id } : { kind: "meeting-summary", meetingId: row.meeting_id };
    meeting.append(viewLink(row.title, target, opts.clients, opts.onNavigate));
    meeting.append(el("div", "pk-card-sub", row.mode === "in_person" ? "In person" : "Online call"));
    const length = el("td", "pk-num figure", lengthLabel(row.length_minutes));
    const status = el("td");
    const line = el("div", "pk-connection-state");
    line.append(statusPill(row.status));
    status.append(line);
    if (row.processing) status.append(el("div", "pk-card-sub", "Preparing notes"));
    if (row.status === "Exception" && row.exception_reason) status.append(el("div", "pk-card-sub", row.exception_reason));
    const cells: Array<[string, HTMLElement]> = [["When", when], ["Meeting", meeting]];
    if (showClient) cells.push(["Client", el("td", undefined, row.client_name)]);
    cells.push(["Length", length], ["Status", status]);
    for (const [label, cell] of cells) {
      cell.dataset.label = label;
      tr.append(cell);
    }
    tbody.append(tr);
  }
  const cap = el("caption", "pk-visually-hidden", caption);
  table.append(cap, colgroup, thead, tbody);
  const wrap = el("div", "pk-table-wrap");
  wrap.append(table);
  section.append(wrap);
  return section;
}

/** The recorder's primary action: Ink, with the one coral dot (R1, R2). */
export function recordButton(label: string, onClick: () => void): HTMLButtonElement {
  const b = el("button", "btn btn-primary pk-record-button");
  b.type = "button";
  b.innerHTML = '<svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" focusable="false"><circle cx="6" cy="6" r="5" fill="currentColor"></circle></svg>';
  b.append(label);
  b.addEventListener("click", onClick);
  return b;
}

/** What happens to a meeting's audio, transcript and notes, linking to the Privacy Policy that sets it out. */
export function retentionNote(lead?: string): HTMLParagraphElement {
  const p = el("p", "muted pk-footnote", lead ? `${lead} ${T.footnote} ` : `${T.footnote} `);
  const a = el("a", undefined, T.privacyPolicy);
  a.href = PRIVACY_POLICY_URL;
  a.target = "_blank";
  a.rel = "noopener";
  p.append(a, ".");
  return p;
}

export function renderMeetingsPage(container: HTMLElement, opts: MeetingsPageOptions): void {
  container.replaceChildren();
  const actions = el("div", "pk-meetings-actions");
  if (opts.onStartInPerson) actions.append(button(T.startInPerson, "btn btn-outline", opts.onStartInPerson));
  actions.append(recordButton("Record a meeting", opts.onRecord));
  container.append(actions);
  if (opts.error) container.append(alertBox(opts.error));
  else if (!opts.list) container.append(el("div", "pk-empty", "Loading…"));
  else {
    // Meetings in the room that haven't ended yet (in person, before the recorder's phone path).
    if (opts.list.upcoming.length) container.append(meetingTable("In progress", opts.list.upcoming, "", "upcoming", opts));
    container.append(
      meetingTable("Notes to review", opts.list.to_review, "No notes to review.", "review", opts),
      meetingTable("Approved", opts.list.approved ?? [], "No approved notes yet.", "approved", opts)
    );
  }
  container.append(retentionNote());
}

// ---- New meeting (and Start in-person meeting) ----

export interface NewMeetingDialogOptions {
  mode: "online" | "in_person";
  clients: readonly FilterOption[];
  selectedClients: readonly string[];
  timeZone: string;
  now?: Date;
  create: (payload: Record<string, unknown>) => Promise<CreatedMeeting>;
  onCreated: () => void; // reload the list
  onStart: (meetingId: string) => void; // in person: straight into the room
}

const LENGTHS = [15, 30, 45, 60, 90, 120];

function field(labelText: string, control: HTMLElement, id: string, hint?: string): HTMLElement {
  const wrap = el("div", "pk-field");
  const label = el("label", undefined, labelText);
  label.htmlFor = id;
  control.id = id;
  wrap.append(label, control);
  if (hint) {
    const h = el("p", "pk-field-hint muted", hint);
    h.id = `${id}-hint`;
    control.setAttribute("aria-describedby", h.id);
    wrap.append(h);
  }
  return wrap;
}

function localDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** The next half hour from `now`, as "hh:mm" (and its date). */
function nextHalfHour(now: Date): { date: string; time: string } {
  const d = new Date(now.getTime());
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() < 30 ? 30 : 60);
  return { date: localDate(d), time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}` };
}

export function openNewMeetingDialog(opts: NewMeetingDialogOptions): HTMLDialogElement {
  const inPerson = opts.mode === "in_person";
  const dialog = el("dialog", "pk-dialog");
  dialog.setAttribute("aria-labelledby", "pk-dialog-title");
  const title = el("h2", "pk-dialog-title", inPerson ? T.startInPerson : T.newMeeting);
  title.id = "pk-dialog-title";
  const form = el("form", "pk-dialog-form");
  form.noValidate = true;

  const titleInput = el("input", "pk-input");
  titleInput.type = "text";
  titleInput.maxLength = 120;
  titleInput.autocomplete = "off";
  const client = el("select", "pk-input");
  if (opts.clients.length !== 1) client.append(new Option("Choose a client", ""));
  for (const c of opts.clients) client.append(new Option(c.client_name, c.client_slug));
  if (opts.selectedClients.length === 1) client.value = opts.selectedClients[0]!;

  const start = nextHalfHour(opts.now ?? new Date());
  const date = el("input", "pk-input");
  date.type = "date";
  date.value = start.date;
  // A list rather than the browser's time box, so it always reads am/pm.
  const time = el("select", "pk-input");
  for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
    const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
    const mm = String(minutes % 60).padStart(2, "0");
    time.append(new Option(clockLabel(minutes), `${hh}:${mm}`));
  }
  time.value = start.time;
  const length = el("select", "pk-input");
  for (const m of LENGTHS) length.append(new Option(lengthLabel(m), String(m)));
  length.value = inPerson ? "60" : "45";

  // Invite by email: chips. Enter, comma, space or leaving the box adds;
  // Backspace in an empty box removes the last one.
  const emails: string[] = [];
  const chips = el("div", "pk-chips");
  const chipList = el("ul", "pk-chip-list");
  chipList.setAttribute("aria-label", "People invited");
  const emailInput = el("input", "pk-chip-input");
  emailInput.type = "email";
  emailInput.autocomplete = "off";
  emailInput.placeholder = "name@example.com";
  const emailError = el("p", "pk-field-error");
  emailError.setAttribute("aria-live", "polite");
  chips.append(chipList, emailInput);
  chips.addEventListener("click", (ev) => {
    if (ev.target === chips) emailInput.focus();
  });
  const drawChips = () => {
    chipList.replaceChildren();
    for (const email of emails) {
      const li = el("li", "pk-chip");
      li.append(el("span", undefined, email));
      const remove = button("×", "pk-chip-remove", () => {
        emails.splice(emails.indexOf(email), 1);
        drawChips();
        emailInput.focus();
      });
      remove.setAttribute("aria-label", `Remove ${email}`);
      li.append(remove);
      chipList.append(li);
    }
  };
  const addTyped = () => {
    const { added, invalid } = parseEmails(emailInput.value, emails);
    emails.push(...added);
    emailInput.value = invalid.join(" ");
    emailError.textContent = invalid.length ? `Check ${invalid.length === 1 ? "this address" : "these addresses"}: ${invalid.join(", ")}` : "";
    drawChips();
  };
  emailInput.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" || ev.key === "," || ev.key === " " || ev.key === ";") {
      if (emailInput.value.trim()) {
        ev.preventDefault();
        addTyped();
      } else if (ev.key === "Enter") ev.preventDefault();
    } else if (ev.key === "Backspace" && !emailInput.value && emails.length) {
      emails.pop();
      drawChips();
    }
  });
  emailInput.addEventListener("blur", () => {
    if (emailInput.value.trim()) addTyped();
  });
  emailInput.addEventListener("paste", () => setTimeout(addTyped, 0));

  const problems = el("div", "pk-dialog-problems");
  problems.setAttribute("role", "alert");

  const tzHint = `Times are in your time zone (${opts.timeZone}).`;
  form.append(field("Title", titleInput, "pk-nm-title"), field("Client", client, "pk-nm-client"));
  if (!inPerson) {
    const row = el("div", "pk-field-row");
    row.append(field("Date", date, "pk-nm-date"), field("Time", time, "pk-nm-time"));
    form.append(row, el("p", "pk-field-hint muted", tzHint));
  }
  form.append(field("Length", length, "pk-nm-length"));
  if (!inPerson) {
    const wrap = el("div", "pk-field");
    const label = el("label", undefined, "Invite by email");
    label.htmlFor = "pk-nm-emails";
    emailInput.id = "pk-nm-emails";
    wrap.append(label, chips, emailError, el("p", "pk-field-hint muted", T.inviteExplainer));
    form.append(wrap);
  }
  form.append(problems);

  const actions = el("div", "pk-dialog-actions");
  const primary = el("button", "btn btn-primary", inPerson ? T.start : T.sendInvites);
  primary.type = "submit";
  const copyInstead = button(T.copyLinkInstead, "btn btn-outline", () => void submit("copy"));
  const cancel = button(T.cancel, "btn btn-outline pk-btn-quiet", () => dialog.close());
  actions.append(primary);
  if (!inPerson) actions.append(copyInstead);
  actions.append(cancel);
  form.append(actions);
  form.addEventListener("submit", (ev) => {
    ev.preventDefault();
    void submit(inPerson ? "start" : "send");
  });

  dialog.append(title, form);
  dialog.addEventListener("close", () => dialog.remove());
  document.body.append(dialog);
  dialog.showModal();
  titleInput.focus();

  let sending = false;
  async function submit(how: "send" | "copy" | "start"): Promise<void> {
    if (sending) return;
    if (emailInput.value.trim()) addTyped();
    const values: NewMeetingForm = {
      title: titleInput.value,
      clientSlug: client.value,
      date: inPerson ? "" : date.value,
      time: inPerson ? "" : time.value,
      lengthMinutes: Number(length.value),
      emails: [...emails],
      mode: opts.mode,
    };
    const found = formProblems(values);
    if (how === "copy" && emails.length === 0) found.push("Add at least one email: each person gets their own link.");
    if (emailError.textContent) found.push("Fix the email addresses first.");
    problems.replaceChildren(...found.map((p) => el("p", undefined, p)));
    if (found.length) return;
    sending = true;
    primary.disabled = copyInstead.disabled = true;
    try {
      const created = await opts.create(createPayload(values, opts.timeZone, opts.now));
      opts.onCreated();
      if (inPerson) {
        dialog.close();
        opts.onStart(created.meeting_id);
      } else {
        showLinks(created, how === "copy" ? "copy" : "send");
      }
    } catch (err) {
      problems.replaceChildren(el("p", undefined, messageOf(err, "The meeting wasn't created. Try again in a few minutes.")));
      sending = false;
      primary.disabled = copyInstead.disabled = false;
    }
  }

  /** After creating: each person's own link, to copy. Nothing is emailed
   * until the server's invite emails are switched on (MEETINGS_COPY.emailsNotLive). */
  function showLinks(created: CreatedMeeting, how: "send" | "copy"): void {
    title.textContent = "Meeting created";
    const body = el("div", "pk-dialog-form");
    body.append(el("p", undefined, `${created.title} · ${formatWhen(created.scheduled_start, opts.timeZone)}`));
    if (how === "send") body.append(el("p", "pk-notice", T.emailsNotLive));
    const list = el("ul", "pk-link-list");
    for (const invite of created.guest_invites) {
      const li = el("li", "pk-link-row");
      li.append(el("span", "pk-link-email", invite.email));
      const status = el("span", "pk-link-status muted");
      status.setAttribute("aria-live", "polite");
      const copy = button("Copy link", "btn btn-outline", () => {
        void copyText(new URL(invite.join_url, location.origin).href).then((ok) => {
          status.textContent = ok ? "Copied" : "Couldn't copy. Select the link and copy it yourself.";
        });
      });
      copy.setAttribute("aria-label", `Copy the link for ${invite.email}`);
      li.append(copy, status);
      list.append(li);
    }
    body.append(list);
    const done = el("div", "pk-dialog-actions");
    const open = button("Open the meeting", "btn btn-primary", () => {
      dialog.close();
      opts.onStart(created.meeting_id);
    });
    done.append(open, button("Done", "btn btn-outline", () => dialog.close()));
    body.append(done);
    form.replaceWith(body);
    open.focus();
  }

  return dialog;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// ---- Tiles, shared by the room and in-person mode ----

/** `recording` null: not known (a guest, before the API says) -- the tile
 * then makes no claim either way. */
function preekieTile(opts: { recording: boolean | null; large?: boolean; caption?: string }): HTMLElement {
  const tile = el("div", `pk-tile pk-tile-preekie${opts.large ? " pk-tile-large" : ""}`);
  const ring = el("div", "pk-preekie-ring");
  const logo = el("img");
  logo.src = "/brand/preekie-icon.svg";
  logo.alt = "";
  logo.width = opts.large ? 96 : 56;
  logo.height = opts.large ? 96 : 56;
  ring.append(logo);
  const caption = opts.caption ?? (opts.recording === null ? "Preekie" : opts.recording ? T.preekieTakingNotes : T.notRecording);
  tile.append(ring, el("p", "pk-tile-caption", caption));
  tile.append(el("span", "pk-tile-name", opts.recording ? T.preekieRecordingLabel : "Preekie"));
  return tile;
}

function personTile(name: string, stream: MediaStream | null, showVideo: boolean, muted: boolean): HTMLElement {
  const tile = el("div", "pk-tile");
  if (stream && showVideo) {
    const video = el("video", "pk-tile-video");
    video.muted = true; // never play your own microphone back
    video.autoplay = true;
    video.playsInline = true;
    video.srcObject = stream;
    tile.append(video);
  } else {
    const avatar = el("div", "pk-tile-avatar", name === "You" ? "You" : initials(name));
    avatar.setAttribute("aria-hidden", "true");
    tile.append(avatar);
  }
  const label = el("span", "pk-tile-name", muted ? `${name} (muted)` : name);
  tile.append(label);
  return tile;
}

// ---- The meeting room (host and members) and in-person mode ----

export interface RoomOptions {
  root: HTMLElement;
  meetingId: string;
  videoAndShare: boolean;
  timeZone: string;
  onLeave: () => void;
  /** Guests only: what the room knows without a member session, and the
   * join they already have (the guest join route, once admitted). */
  guest?: { title: string; name: string; join: JoinInfo; token: string };
  /** From the lobby: start with the camera on, or muted. */
  startWithCamera?: boolean;
  startMuted?: boolean;
  /** Tests and screenshots: a fixed clock. */
  now?: () => Date;
}

export interface Mounted {
  destroy(): void;
}

const ENDED_MESSAGES: Record<string, string> = {
  ended: "The host ended the meeting.",
  elsewhere: "You joined this meeting from another device or tab, so this one has left.",
  failed: "The connection to the meeting dropped. Reload the page to rejoin.",
};

function videoTile(name: string, video: HTMLVideoElement, extra = ""): HTMLElement {
  const tile = el("div", `pk-tile${extra}`);
  tile.append(video, el("span", "pk-tile-name", name));
  // Moving a playing <video> between redraws can pause it; carry on.
  if (video.paused) void video.play().catch(() => undefined);
  return tile;
}

export function mountRoom(opts: RoomOptions): Mounted {
  const now = opts.now ?? (() => new Date());
  let call: ChimeSession | null = null;
  let connecting = true;
  let mediaError: string | null = null;
  let ended: string | null = null;
  let detail: MeetingDetail | null = null;
  let lobby: LobbyGuest[] = [];
  let error: string | null = null;
  let busy = false;
  let agreed = false; // in person: "I've told everyone and they've agreed."
  let confirmingEnd = false;
  let destroyed = false;
  const timers: number[] = [];
  const live = el("div", "pk-visually-hidden");
  live.setAttribute("role", "status");
  let lastRecording: string | null = null;
  const isGuest = !!opts.guest;
  // A guest can't read the member routes; their own invite route lists
  // who's in the room once they're admitted (names and opaque ids only).
  let guestParticipants: NonNullable<GuestLobbyInfo["participants"]> = [];
  let guestInfo: GuestLobbyInfo | null = null;

  async function pollAsGuest(): Promise<void> {
    if (!opts.guest || destroyed || ended) return;
    try {
      guestInfo = await fetchGuestLobby(opts.meetingId, opts.guest.token);
      guestParticipants = guestInfo.participants ?? [];
      draw();
    } catch {
      // Names fall back to Guest/Member; the call itself carries on.
    }
  }

  const say = (text: string) => {
    live.textContent = text;
  };

  async function poll(): Promise<void> {
    if (isGuest || destroyed || ended) return;
    try {
      detail = await fetchMeeting(opts.meetingId);
      if (detail.is_host && detail.mode === "online") lobby = (await fetchLobby(opts.meetingId)).waiting;
      error = null;
    } catch (err) {
      error = messageOf(err, "Preekie lost touch with the meeting. Check your connection.");
    }
    const rec = detail?.recording_status ?? null;
    if (lastRecording !== null && rec !== lastRecording) say(rec === "recording" ? "Recording started" : "Recording stopped");
    lastRecording = rec;
    draw();
  }

  async function act(fn: () => Promise<unknown>, fallback: string): Promise<void> {
    if (busy) return;
    busy = true;
    error = null;
    draw();
    try {
      await fn();
      await poll();
    } catch (err) {
      error = messageOf(err, fallback);
    }
    busy = false;
    draw();
  }

  /** Camera, screen share: a refusal is explained, never thrown away. */
  async function media(fn: () => Promise<void>): Promise<void> {
    mediaError = null;
    try {
      await fn();
    } catch (err) {
      mediaError = mediaErrorMessage(err);
    }
    draw();
  }

  const toggleRecording = () => {
    if (!detail) return;
    const on = detail.recording_status === "recording";
    // One button, no confirmation (the mockup): Start or Stop.
    void act(
      () => (on ? stopRecording(opts.meetingId) : startRecording(opts.meetingId, detail!.mode === "in_person" ? agreed : undefined)),
      on ? "Recording didn't stop. Try again." : "Recording didn't start. Try again."
    );
  };

  const leave = () => {
    destroy();
    opts.onLeave();
  };

  async function connect(): Promise<void> {
    try {
      const join = opts.guest?.join ?? (await joinMeeting(opts.meetingId));
      // Loaded only here, so pages without a meeting never download the SDK.
      const { ChimeSession: Session } = await import("./chime.js");
      if (destroyed) return;
      call = new Session(join, {
        onChange: draw,
        onEnded: (reason) => {
          if (reason === "left") return;
          ended = ENDED_MESSAGES[reason] ?? ENDED_MESSAGES.failed!;
          timers.forEach((t) => clearInterval(t));
          draw();
        },
      });
      const inPerson = detail?.mode === "in_person";
      await call.start(opts.videoAndShare && !inPerson && (opts.startWithCamera ?? true));
      if (opts.startMuted) call.setMuted(true);
    } catch (err) {
      // For whoever is diagnosing a failed join (the browser console only).
      console.warn("Preekie meeting: couldn't connect", err);
      const name = (err as { name?: string } | null)?.name ?? "";
      mediaError =
        err instanceof ApiError
          ? messageOf(err, "Preekie couldn't join you to the meeting.")
          : /NotAllowed|PermissionDenied|NotFound|NotReadable|Security|Overconstrained/.test(name)
            ? mediaErrorMessage(err)
            : "Preekie couldn't connect you to the meeting. Check your connection, then reload the page.";
    }
    connecting = false;
    draw();
  }

  function controls(): HTMLElement {
    const bar = el("div", "pk-room-controls");
    bar.setAttribute("role", "toolbar");
    bar.setAttribute("aria-label", "Meeting controls");
    const muted = call?.muted ?? false;
    const mute = button(muted ? T.unmute : T.mute, "pk-control", () => call?.setMuted(!muted));
    mute.setAttribute("aria-pressed", String(muted));
    mute.disabled = !call;
    bar.append(mute);
    if (opts.videoAndShare && detail?.mode !== "in_person") {
      const cameraOn = call?.cameraOn ?? false;
      const cam = button(T.camera, "pk-control", () => void media(() => call!.setCamera(!cameraOn)));
      cam.setAttribute("aria-pressed", String(!cameraOn));
      cam.setAttribute("aria-label", cameraOn ? "Turn camera off" : "Turn camera on");
      cam.disabled = !call;
      const sharing = call?.sharing ?? false;
      const share = button(sharing ? "Stop sharing" : T.shareScreen, "pk-control", () => void media(() => call!.setSharing(!sharing)));
      share.setAttribute("aria-pressed", String(sharing));
      share.disabled = !call;
      bar.append(cam, share);
    }
    if (canRunMeeting(detail?.is_host ?? false) && detail && !(detail.mode === "in_person" && detail.recording_status !== "recording")) {
      const on = detail.recording_status === "recording";
      const rec = button(on ? T.stopRecording : T.startRecording, `pk-control${on ? " pk-control-on" : ""}`, toggleRecording);
      rec.disabled = busy || detail.recording_status === "concatenating";
      bar.append(rec);
    }
    bar.append(button(T.leave, "pk-control pk-control-leave", leave));
    if (canRunMeeting(detail?.is_host ?? false)) {
      if (confirmingEnd) {
        const box = el("div", "pk-end-confirm");
        box.append(
          el("span", undefined, "End the meeting for everyone?"),
          button("End meeting", "btn btn-destructive", () => {
            confirmingEnd = false;
            void act(() => endMeeting(opts.meetingId), "The meeting didn't end. Try again.").then(() => {
              if (!error) leave();
            });
          }),
          button("Keep going", "btn btn-outline", () => {
            confirmingEnd = false;
            draw();
          })
        );
        bar.append(box);
      } else {
        bar.append(
          button(T.endMeeting, "pk-control pk-control-end", () => {
            confirmingEnd = true;
            draw();
          })
        );
      }
    }
    return bar;
  }

  function topBar(): HTMLElement {
    const bar = el("header", "pk-room-top");
    const lead = el("div", "pk-room-title");
    lead.append(el("h1", "pk-title", detail?.title ?? opts.guest?.title ?? "Meeting"));
    if (detail) lead.append(el("span", "muted", detail.client_name));
    bar.append(lead);
    if (detail) {
      const on = detail.recording_status === "recording";
      const rec = el("div", `pk-recording${on ? " pk-recording-on" : ""}`);
      if (on) rec.append(el("span", "pk-recording-dot"));
      const label = recordingLabel(detail.recording_status, detail.recording_started_at, now());
      if (on) {
        const [word, time] = label.split(" · ");
        rec.append(el("span", undefined, `${word} · `), el("span", "figure pk-recording-time", time));
        rec.setAttribute("aria-label", "Recording");
      } else rec.append(el("span", undefined, label));
      bar.append(rec);
    }
    return bar;
  }

  function sidePanel(): HTMLElement {
    const side = el("aside", "pk-room-side");
    side.setAttribute("aria-label", "People");
    const h = el("h2", "pk-room-side-title", T.inTheMeeting);
    const list = el("ul", "pk-people");
    for (const p of detail?.participants ?? []) list.append(el("li", undefined, p.display_name));
    if (!detail?.participants.length) list.append(el("li", "muted", "Nobody yet"));
    side.append(h, list);
    if (detail && canRunMeeting(detail.is_host) && detail.mode === "online") {
      const lh = el("h2", "pk-room-side-title", T.waitingInLobby);
      const ll = el("ul", "pk-people pk-lobby-list");
      for (const g of lobby) {
        const li = el("li", "pk-lobby-row");
        li.append(el("span", undefined, g.display_name));
        const admit = button(T.admit, "btn btn-outline", () =>
          void act(() => admitGuest(opts.meetingId, g.invite_id), `${g.display_name} wasn't let in. Try again.`)
        );
        admit.setAttribute("aria-label", `Admit ${g.display_name}`);
        admit.disabled = busy;
        li.append(admit);
        ll.append(li);
      }
      if (!lobby.length) ll.append(el("li", "muted", "Nobody is waiting"));
      side.append(lh, ll);
    }
    return side;
  }

  function inPersonPanel(): HTMLElement {
    const side = el("aside", "pk-room-side");
    side.setAttribute("aria-labelledby", "pk-before");
    const h = el("h2", "pk-room-side-title", T.beforeYouStart);
    h.id = "pk-before";
    side.append(h, el("blockquote", "pk-notice-quote", RECORDING_NOTICE), el("p", "muted", T.showOrRead));
    const label = el("label", "pk-check");
    const box = el("input");
    box.type = "checkbox";
    box.checked = agreed;
    const start = button(T.start, "btn btn-primary pk-wide", toggleRecording);
    // Recording needs this device's microphone in the meeting first.
    start.disabled = !agreed || busy || !call;
    box.addEventListener("change", () => {
      agreed = box.checked;
      start.disabled = !agreed || busy || !call;
    });
    label.append(box, el("span", undefined, T.everyoneAgreed));
    side.append(label, start);
    return side;
  }

  function stageTiles(stage: HTMLElement, recording: boolean): void {
    const participants = isGuest ? guestParticipants : detail?.participants ?? [];
    // Screen shares first and full width: that's what everyone is looking at.
    for (const tile of call?.tiles.values() ?? []) {
      if (tile.content) {
        const owner = attendeeName(tile.externalUserId, participants);
        stage.append(videoTile(`${owner}'s screen`, tile.video, " pk-tile-share"));
      }
    }
    const guestRecording = guestInfo?.recording_status ? guestInfo.recording_status === "recording" : null;
    stage.append(preekieTile({ recording: isGuest ? guestRecording : recording }));
    const selfName = isGuest ? `${opts.guest!.name} (you)` : "You";
    const muted = call?.muted ?? false;
    const ownCamera = [...(call?.tiles.values() ?? [])].find((t) => t.local && !t.content);
    if (ownCamera && call?.cameraOn) stage.append(videoTile(muted ? `${selfName} (muted)` : selfName, ownCamera.video));
    else stage.append(personTile(selfName, null, false, muted));
    if (call?.sharing) {
      const note = el("div", "pk-tile pk-tile-note");
      note.append(el("p", "pk-tile-caption", "You're sharing your screen"));
      stage.append(note);
    }
    for (const remote of call?.remotes.values() ?? []) {
      const name = attendeeName(remote.externalUserId, participants);
      const camera = [...call!.tiles.values()].find((t) => !t.content && t.attendeeId === remote.attendeeId);
      stage.append(camera ? videoTile(name, camera.video) : personTile(name, null, false, false));
    }
  }

  function draw(): void {
    if (destroyed) return;
    const page = el("div", "pk-room");
    page.append(topBar());
    if (ended) {
      const box = el("div", "pk-room-ended");
      box.setAttribute("role", "status");
      box.append(el("p", undefined, ended), button(isGuest ? "Close" : "Back to Meetings", "btn btn-outline", leave));
      page.append(box, live);
      opts.root.replaceChildren(page);
      return;
    }
    const inPerson = detail?.mode === "in_person" && detail.is_host;
    const recording = detail?.recording_status === "recording";
    if (error) page.append(alertBox(error));
    if (mediaError) page.append(alertBox(mediaError));
    if (connecting) {
      const c = el("p", "pk-room-connecting", "Connecting you to the meeting…");
      c.setAttribute("role", "status");
      page.append(c);
    }
    const body = el("div", "pk-room-body");
    const stage = el("main", "pk-room-stage");
    stage.setAttribute("aria-label", "Video");
    if (inPerson) {
      stage.classList.add("pk-room-stage-single");
      stage.append(preekieTile({ recording, large: true, caption: recording ? T.preekieTakingNotes : T.readyToTakeNotes }));
      body.append(stage, recording ? sidePanel() : inPersonPanel());
    } else {
      stageTiles(stage, recording);
      body.append(stage);
      if (!isGuest) body.append(sidePanel());
    }
    page.append(body, controls(), live);
    // Keep keyboard focus on the same control across redraws.
    const focused = document.activeElement as HTMLElement | null;
    const key = focused && opts.root.contains(focused) ? focused.textContent : null;
    opts.root.replaceChildren(page);
    if (key) {
      const again = Array.from(opts.root.querySelectorAll<HTMLElement>("button")).find((b) => b.textContent === key);
      again?.focus();
    }
  }

  function destroy(): void {
    if (destroyed) return;
    destroyed = true;
    timers.forEach((t) => clearInterval(t));
    call?.leave();
  }

  draw();
  if (!isGuest) {
    // The meeting's details first (in person or online decides the camera).
    void poll().then(connect);
    timers.push(window.setInterval(() => void poll(), POLL_MS));
  } else {
    void connect();
    void pollAsGuest();
    timers.push(window.setInterval(() => void pollAsGuest(), POLL_MS));
  }
  // The recording timer ticks every second between polls.
  timers.push(
    window.setInterval(() => {
      if (detail?.recording_status === "recording") {
        const rec = opts.root.querySelector(".pk-recording-time");
        if (rec) rec.textContent = recordingLabel(detail.recording_status, detail.recording_started_at, now()).split(" · ")[1] ?? "";
      }
    }, 1000)
  );
  return { destroy };
}

// ---- The guest lobby (desktop and phone) ----

export interface GuestOptions {
  root: HTMLElement;
  meetingId: string;
  token: string;
  timeZone: string;
  videoAndShare: boolean;
}

export function mountGuest(opts: GuestOptions): Mounted {
  const media = new LocalMedia(opts.videoAndShare);
  let info: GuestLobbyInfo | null = null;
  let stage: "loading" | "lobby" | "waiting" | "room" | "left" | "invalid" = "loading";
  let error: string | null = null;
  let name = "";
  let consent = false;
  let sending = false;
  let room: Mounted | null = null;
  let timer = 0;
  let destroyed = false;

  function card(): HTMLElement {
    const c = el("section", "pk-lobby-card");
    const wordmark = el("img", "pk-auth-wordmark");
    wordmark.src = "/brand/preekie-wordmark-ink.svg";
    wordmark.alt = "Preekie";
    c.append(wordmark);
    return c;
  }

  function draw(): void {
    if (destroyed || stage === "room") return;
    const page = el("main", "pk-lobby");
    if (stage === "loading") {
      page.append(el("div", "pk-empty", "Loading…"));
    } else if (stage === "invalid" || stage === "left") {
      const c = card();
      const h = el("h1", "pk-auth-heading", stage === "left" ? "You've left the meeting" : "This link doesn't work");
      c.append(h, el("p", "pk-auth-lead", error ?? "You can close this page."));
      page.append(c);
    } else if (info) {
      const preview = el("div", "pk-lobby-preview");
      preview.append(personTile(name.trim() || "You", media.stream, media.cameraOn, !media.micOn));
      const toggles = el("div", "pk-lobby-toggles");
      const mic = button(media.micOn ? "Microphone on" : "Microphone off", "pk-control", () => {
        media.setMic(!media.micOn);
        draw();
      });
      mic.setAttribute("aria-pressed", String(media.micOn));
      toggles.append(mic);
      if (opts.videoAndShare) {
        const cam = button(media.cameraOn ? "Camera on" : "Camera off", "pk-control", () => {
          media.setCamera(!media.cameraOn);
          draw();
        });
        cam.setAttribute("aria-pressed", String(media.cameraOn));
        toggles.append(cam);
      }
      preview.append(toggles);
      if (media.error) preview.append(alertBox(media.error));

      const c = card();
      c.append(el("p", "pk-lobby-firm", info.firm));
      const h = el("h1", "pk-auth-heading", info.title);
      h.id = "pk-lobby-title";
      c.append(h, el("p", "pk-lobby-when", formatWhen(info.scheduled_start, opts.timeZone)));
      if (stage === "waiting") {
        const wait = el("p", "pk-notice", T.waitingForHost);
        wait.setAttribute("role", "status");
        c.append(wait);
      } else {
        const form = el("form", "pk-auth-form");
        form.noValidate = true;
        const label = el("label", "pk-auth-label", T.yourName);
        label.htmlFor = "pk-guest-name";
        const input = el("input", "pk-auth-input");
        input.id = "pk-guest-name";
        input.autocomplete = "name";
        input.maxLength = 60;
        input.value = name;
        const notice = el("p", "pk-lobby-notice", info.notice);
        notice.id = "pk-guest-notice";
        const check = el("label", "pk-check");
        const box = el("input");
        box.type = "checkbox";
        box.checked = consent;
        box.setAttribute("aria-describedby", notice.id);
        check.append(box, el("span", undefined, T.iAgree));
        const join = el("button", "btn btn-primary pk-auth-submit", sending ? "Joining…" : T.join);
        join.type = "submit";
        const update = () => {
          join.disabled = sending || !canJoin(consent, name);
        };
        input.addEventListener("input", () => {
          name = input.value;
          update();
        });
        box.addEventListener("change", () => {
          consent = box.checked;
          update();
        });
        update();
        form.addEventListener("submit", (ev) => {
          ev.preventDefault();
          void enter();
        });
        const privacy = el("a", "pk-auth-link", "Privacy Policy");
        privacy.href = PRIVACY_POLICY_URL;
        privacy.target = "_blank";
        privacy.rel = "noopener";
        form.append(label, input, notice, check);
        if (error) form.append(alertBox(error));
        form.append(join);
        c.append(form, privacy);
      }
      page.append(preview, c);
    }
    opts.root.replaceChildren(page);
  }

  async function enter(): Promise<void> {
    if (!canJoin(consent, name) || sending) return;
    sending = true;
    error = null;
    draw();
    try {
      await enterGuestLobby(opts.meetingId, opts.token, name.trim());
      stage = "waiting";
      waitToBeAdmitted();
    } catch (err) {
      error = messageOf(err, "You weren't added to the lobby. Try again.");
    }
    sending = false;
    draw();
  }

  function waitToBeAdmitted(): void {
    const attempt = async () => {
      try {
        const join = await guestJoin(opts.meetingId, opts.token);
        clearInterval(timer);
        openRoom(join);
      } catch (err) {
        if (err instanceof ApiError && err.code === "not_admitted_yet") return; // keep waiting
        clearInterval(timer);
        stage = "invalid";
        error = messageOf(err, "Preekie couldn't let you in. Ask the host for a new link.");
        draw();
      }
    };
    void attempt();
    timer = window.setInterval(() => void attempt(), POLL_MS);
  }

  function openRoom(join: JoinInfo): void {
    stage = "room";
    // The room's Chime session takes the microphone and camera itself,
    // starting the way the guest left them in the lobby.
    const startWithCamera = media.cameraOn;
    const startMuted = !media.micOn;
    media.stop();
    room = mountRoom({
      root: opts.root,
      meetingId: opts.meetingId,
      videoAndShare: opts.videoAndShare,
      timeZone: opts.timeZone,
      guest: { title: info?.title ?? "Meeting", name: name.trim(), join, token: opts.token },
      startWithCamera,
      startMuted,
      onLeave: () => {
        stage = "left";
        error = "You can close this page.";
        draw();
      },
    });
  }

  void (async () => {
    try {
      info = await fetchGuestLobby(opts.meetingId, opts.token);
      document.title = `${info.title} · Preekie`;
      if (info.status === "invited") stage = "lobby";
      else {
        stage = "waiting";
        name = name || "";
        waitToBeAdmitted();
      }
      draw();
      await media.start();
    } catch (err) {
      stage = "invalid";
      error =
        err instanceof ApiError && err.status === 410
          ? "This link has expired. Ask the person who invited you for a new one."
          : "This meeting link isn't valid. Check you've used the whole link from your invitation.";
    }
    draw();
  })();
  draw();

  return {
    destroy() {
      destroyed = true;
      clearInterval(timer);
      media.stop();
      room?.destroy();
    },
  };
}

// ---- Summary review ----

export interface SummaryOptions {
  root: HTMLElement;
  meetingId: string;
  timeZone: string;
  canReviewIfAdmin: boolean; // account admin (Me.can_view_onboarding); the host can too
  onNavigate: (view: View) => void;
  /** CAP-009: show the recorder's name in place of "Host" (the new console only; today's console is unchanged). */
  recorderName?: boolean;
}

/** The AI-draft label: in view mode, edit mode and next to Approve. Its first
 * sentence is the label itself; the rest says what to do. */
function aiDraftNotice(): HTMLElement {
  const box = el("p", "pk-ai-notice");
  const [first, ...rest] = AI_DRAFT_NOTICE.split(". ");
  box.append(el("strong", undefined, `${first}.`), ` ${rest.join(". ")}`);
  return box;
}

function flag(text: string, tone: "exception" | "review" | "neutral"): HTMLElement {
  return el("span", `pk-flag pk-flag-${tone}`, text);
}

/** Under each item: its supporting quote and what the check found. An item
 * with no source is flagged, never hidden. */
function itemReview(item: ReviewItem, isAction: boolean): HTMLElement[] {
  const out: HTMLElement[] = [];
  const check = itemCheck(item);
  if (check.kind === "unverified") {
    const line = el("p", "pk-item-check");
    line.append(flag(check.label, "exception"), ` ${check.reason}`);
    out.push(line);
  } else if (check.kind === "edited") {
    const line = el("p", "pk-item-check");
    line.append(flag(check.label, "neutral"), ` ${check.note}`);
    out.push(line);
  }
  if ((check.kind === "verified" || check.kind === "unverified") && check.quote) {
    const q = el("blockquote", "pk-item-quote");
    q.append(el("span", "pk-visually-hidden", check.kind === "verified" ? "Source in the transcript: " : "Quoted: "));
    q.append(`\u201c${check.quote}\u201d`);
    out.push(q);
  }
  for (const d of item.date_inferred ?? []) {
    const line = el("p", "pk-item-check");
    if (isAction) {
      // The action's due line already shows the words and the worked-out date.
      line.append(flag("Date worked out", "review"), " Check the date shown against what was said.");
    } else {
      const shown = inferredDateLine(d);
      line.append(flag("Date worked out", "review"), ` ${shown.words} `, el("span", "figure", shown.date));
    }
    out.push(line);
  }
  return out;
}

function reviewListCard(
  titleText: string,
  plain: readonly string[],
  items: readonly ReviewItem[] | undefined,
  empty: string
): HTMLElement {
  const card = el("section", "pk-settings-card pk-summary-card");
  card.append(el("h2", "pk-section-title", titleText));
  const rows = items?.length ? items : plain.map((text) => ({ text, verified: null }) as ReviewItem);
  if (!rows.length) {
    card.append(el("p", "muted", empty));
    return card;
  }
  const ul = el("ul", "pk-summary-list pk-review-list");
  for (const item of rows) {
    const li = el("li");
    li.append(el("span", "pk-item-text", item.text), ...(items?.length ? itemReview(item, false) : []));
    ul.append(li);
  }
  card.append(ul);
  return card;
}

export function mountSummary(opts: SummaryOptions): Mounted {
  let summary: MeetingSummary | null = null;
  let detail: MeetingDetail | null = null;
  let members: Array<{ user_id: string; display_name: string }> | null = null;
  let error: string | null = null;
  let actionError: string | null = null;
  let notice: string | null = null;
  let editing = false;
  let busy = false;
  let handOverTo = "";
  let note = "";
  let transcript: Array<{ speaker: string; text: string; is_host?: boolean }> | null = null;
  let transcriptHost: string | null = null;
  let transcriptPauses: Array<{ before_turn: number; from: string; to: string | null }> = [];
  const named = opts.recorderName === true;
  let focusTurn: number | null = null; // a "Figures not in the notes" turn to show in the transcript
  let destroyed = false;

  const canReview = () => canReviewNotes(detail?.is_host ?? false, opts.canReviewIfAdmin, summary?.status ?? "");

  function editor(s: MeetingSummary): HTMLElement {
    const form = el("form", "pk-summary-edit");
    form.append(aiDraftNotice());
    const areas: Record<string, HTMLTextAreaElement> = {};
    for (const [key, label] of [
      ["decisions", T.decisions],
      ["figures_and_deadlines", T.figuresAndDeadlines],
      ["open_questions", T.openQuestions],
    ] as const) {
      const ta = el("textarea");
      ta.rows = 4;
      ta.value = (s[key] as string[]).join("\n");
      areas[key] = ta;
      form.append(field(label, ta, `pk-edit-${key}`, "One per line."));
    }
    const actions: MeetingAction[] = s.actions.map((a) => ({ ...a }));
    const table = el("div", "pk-edit-actions");
    const drawActions = () => {
      table.replaceChildren(el("p", "pk-field-label", T.actions));
      actions.forEach((a, i) => {
        const row = el("div", "pk-edit-action");
        const text = el("input", "pk-input");
        text.value = a.text;
        text.setAttribute("aria-label", `Action ${i + 1}`);
        text.addEventListener("input", () => (a.text = text.value));
        const owner = el("input", "pk-input");
        owner.value = a.owner ?? "";
        owner.setAttribute("aria-label", `Owner for action ${i + 1}`);
        owner.addEventListener("input", () => (a.owner = owner.value || null));
        const due = el("input", "pk-input");
        due.type = "date";
        due.value = a.due_date ?? "";
        due.setAttribute("aria-label", `Due date for action ${i + 1}`);
        due.addEventListener("input", () => (a.due_date = due.value || null));
        const remove = button("Remove", "btn btn-outline", () => {
          actions.splice(i, 1);
          drawActions();
        });
        remove.setAttribute("aria-label", `Remove action ${i + 1}`);
        row.append(text, owner, due, remove);
        table.append(row);
      });
      table.append(
        button("Add an action", "btn btn-outline", () => {
          actions.push({ text: "", owner: null, due_date: null });
          drawActions();
        })
      );
    };
    drawActions();
    form.append(table);
    const lines = (ta: HTMLTextAreaElement) => ta.value.split("\n").map((l) => l.trim()).filter(Boolean);
    const buttons = el("div", "pk-actions");
    const save = el("button", "btn btn-primary", "Save changes");
    save.type = "submit";
    buttons.append(save, button(T.cancel, "btn btn-outline", () => ((editing = false), draw())));
    form.append(buttons);
    form.addEventListener("submit", (ev) => {
      ev.preventDefault();
      void run(async () => {
        summary = await editSummary(opts.meetingId, {
          decisions: lines(areas.decisions!),
          actions: actions.filter((a) => a.text.trim()),
          figures_and_deadlines: lines(areas.figures_and_deadlines!),
          open_questions: lines(areas.open_questions!),
        });
        editing = false;
        notice = "Your changes are saved. Preekie's original version is kept for the record.";
      }, "Your changes weren't saved. They're still here; try again.");
    });
    return form;
  }

  async function run(fn: () => Promise<void>, fallback: string): Promise<void> {
    if (busy) return;
    busy = true;
    actionError = null;
    draw();
    try {
      await fn();
    } catch (err) {
      actionError = messageOf(err, fallback);
    }
    busy = false;
    draw();
  }

  async function showTurn(turn: number): Promise<void> {
    focusTurn = turn;
    if (!transcript) {
      await run(async () => {
        const got = await fetchTranscript(opts.meetingId);
          transcript = got.turns;
          transcriptHost = got.host_name ?? null;
          transcriptPauses = got.pauses ?? [];
      }, "Preekie couldn't load the transcript. Try again in a few minutes.");
    } else draw();
    const line = opts.root.querySelector<HTMLElement>(`#pk-turn-${turn}`);
    line?.scrollIntoView({ block: "center" });
    line?.focus();
  }

  /** Next to Approve: the AI-draft label, what the checks found, and every
   * figure said in the meeting that no item contains. */
  function reviewChecks(s: MeetingSummary): HTMLElement[] {
    const out: HTMLElement[] = [aiDraftNotice()];
    const counts = reviewCounts(s);
    const unverified = el("p", "pk-review-count");
    if ((s.unverified_count ?? 0) > 0) unverified.append(flag("No source found", "exception"), " ");
    unverified.append(counts.unverified);
    out.push(unverified);
    if (counts.inferred) {
      const inferred = el("p", "pk-review-count");
      inferred.append(flag("Date worked out", "review"), ` ${counts.inferred}`);
      out.push(inferred);
    }
    if (s.figures_not_in_notes) {
      const box = el("section", "pk-figures-missing");
      box.setAttribute("aria-labelledby", "pk-figures-missing");
      const h = el("h3", "pk-figures-missing-title", counts.figures);
      h.id = "pk-figures-missing";
      box.append(h);
      if (!s.figures_not_in_notes.length) {
        box.append(el("p", "muted", "None. Every figure said in the meeting is in the notes."));
      } else {
        box.append(el("p", "muted pk-figures-missing-help", "Said in the meeting but in none of the items. Check whether any matter."));
        const ul = el("ul", "pk-figures-missing-list");
        for (const f of s.figures_not_in_notes) {
          const li = el("li");
          li.append(el("span", "pk-figure-said", `\u201c${f.said}\u201d`));
          const src = figureSource(f);
          const meta = el("span", "pk-card-sub");
          const turn = button(src.turn, "pk-turn-link", () => void showTurn(f.turn));
          turn.setAttribute("aria-label", `Show ${src.turn} in the transcript`);
          meta.append(`${src.speaker} · `, turn);
          li.append(meta);
          ul.append(li);
        }
        box.append(ul);
      }
      out.push(box);
    }
    return out;
  }

  function handOverCard(s: MeetingSummary): HTMLElement {
    const card = el("section", "pk-settings-card pk-summary-card");
    card.setAttribute("aria-labelledby", "pk-handover");
    const h = el("h2", "pk-section-title", T.handOver);
    h.id = "pk-handover";
    card.append(h);
    if (s.status === "Approved") {
      card.append(el("p", undefined, "Approved and handed over."));
      const who = [s.approved_by_name && `Approved by ${s.approved_by_name}`, s.handed_over_to_name && `handed over to ${s.handed_over_to_name}`]
        .filter(Boolean)
        .join(", ");
      if (who) card.append(el("p", "muted", `${who}.`));
      return card;
    }
    if (!canReview()) {
      if (s.status === "Awaiting review" && s.summary_ready) card.append(...reviewChecks(s));
      card.append(
        el(
          "p",
          "muted",
          s.status === "Awaiting review"
            ? "The meeting's host or an account admin approves these notes."
            : "The notes can be handed over once Preekie has prepared them."
        )
      );
      return card;
    }
    card.append(...reviewChecks(s));
    const select = el("select", "pk-input");
    select.append(new Option(members ? "Choose a person" : "Loading…", ""));
    for (const m of members ?? []) select.append(new Option(m.display_name, m.user_id));
    select.value = handOverTo;
    select.addEventListener("change", () => (handOverTo = select.value));
    const ta = el("textarea");
    ta.rows = 3;
    ta.maxLength = 1000;
    ta.value = note;
    ta.addEventListener("input", () => (note = ta.value));
    card.append(field(T.handOverTo, select, "pk-handover-to"), field(T.note, ta, "pk-handover-note"));
    if (actionError) card.append(alertBox(actionError));
    const approve = button(T.approveAndHandOver, "btn btn-primary pk-wide", () => {
      if (!handOverTo) {
        actionError = "Choose who to hand the notes over to.";
        draw();
        return;
      }
      void run(async () => {
        await approveSummary(opts.meetingId, handOverTo, note.trim());
        summary = await fetchSummary(opts.meetingId);
        notice = "Approved and handed over.";
      }, "The notes weren't approved. Try again in a few minutes.");
    });
    approve.disabled = busy || editing;
    const edit = button(T.editSummary, "btn btn-outline pk-wide", () => {
      editing = true;
      draw();
    });
    edit.disabled = busy || editing;
    card.append(approve, edit);
    return card;
  }

  function transcriptCard(s: MeetingSummary): HTMLElement {
    const card = el("section", "pk-settings-card pk-summary-card");
    card.append(el("h2", "pk-section-title", T.transcriptExcerpt));
    if (!s.transcript_excerpt.length) card.append(el("p", "muted", "The transcript appears once the notes are ready."));
    if (named && s.transcript_excerpt_items) {
      for (const it of s.transcript_excerpt_items) {
        const p = el("p", "pk-transcript-line");
        const who = speakerLabel(it.speaker, it.is_host, s.details.host_name, true);
        if (who) p.append(el("strong", undefined, `${who}: `));
        p.append(it.text);
        card.append(p);
      }
    } else for (const line of s.transcript_excerpt) card.append(el("p", "pk-transcript-line", line));
    if (s.transcript_url) {
      const open = button(T.openFullTranscript, "btn btn-outline", () =>
        void run(async () => {
          const got = await fetchTranscript(opts.meetingId);
          transcript = got.turns;
          transcriptHost = got.host_name ?? null;
          transcriptPauses = got.pauses ?? [];
        }, "Preekie couldn't load the transcript. Try again in a few minutes.")
      );
      card.append(open);
    }
    if (transcript) {
      const full = el("div", "pk-transcript-full");
      full.setAttribute("tabindex", "0");
      full.setAttribute("aria-label", "Full transcript");
      // CAP-009 (D's condition 3): where the recording was paused, between turns (new console only).
      const marker = (i: number) => {
        if (!named) return;
        for (const g of transcriptPauses.filter((x) => x.before_turn === i)) full.append(el("p", "muted pk-transcript-pause", pauseMarker(g.from, g.to, opts.timeZone)));
      };
      transcript.forEach((t, i) => {
        marker(i);
        const p = el("p", `pk-transcript-line${i === focusTurn ? " pk-transcript-focus" : ""}`);
        p.id = `pk-turn-${i}`;
        p.tabIndex = -1;
        p.append(el("strong", undefined, `${speakerLabel(t.speaker, t.is_host, transcriptHost, named)}: `), t.text);
        full.append(p);
      });
      marker(transcript.length);
      card.append(full);
    }
    return card;
  }

  function draw(): void {
    if (destroyed) return;
    const page = el("div", "pk-summary-page");
    if (error) page.append(alertBox(error));
    else if (!summary) page.append(el("div", "pk-empty", "Loading…"));
    else {
      const s = summary;
      const head = el("div", "pk-summary-head");
      head.append(statusPill(s.status));
      const d = s.details;
      head.append(
        el("p", "pk-summary-details", `${d.title} · ${d.client_name} · ${formatWhen(d.scheduled_start, opts.timeZone)} · ${lengthLabel(d.length_minutes)} · Host: ${d.host_name}`)
      );
      page.append(head);
      if (notice) {
        const n = el("p", "pk-notice", notice);
        n.setAttribute("role", "status");
        page.append(n);
      }
      if (s.status === "Exception" && s.exception_reason) page.append(alertBox(s.exception_reason));
      const grid = el("div", "pk-summary-grid");
      const main = el("div", "pk-summary-main");
      if (!s.summary_ready) {
        main.append(el("p", "pk-notice", T.preparing));
      } else if (editing) {
        main.append(editor(s));
      } else {
        if (s.status === "Approved") main.append(el("p", "pk-draft-notice", "Approved notes."));
        else main.append(aiDraftNotice());
        const items = s.items;
        main.append(reviewListCard(T.decisions, s.decisions, items?.decisions, "No decisions noted."));
        const actions = el("section", "pk-settings-card pk-summary-card");
        actions.append(el("h2", "pk-section-title", T.actions));
        if (!s.actions.length) actions.append(el("p", "muted", "No actions noted."));
        else {
          const ul = el("ul", "pk-summary-list pk-summary-actions pk-review-list");
          s.actions.forEach((a, i) => {
            const li = el("li");
            li.append(el("span", "pk-item-text", a.text));
            const meta = el("span", "pk-card-sub");
            const due = actionDue(a);
            meta.append(`${named && a.owner_is_host ? speakerLabel(a.owner, true, s.details.host_name, true) : (a.owner ?? "No owner")} · ${due.words}`);
            if (due.date && due.bracketed) meta.append(" (", el("span", "figure", due.date), ")");
            else if (due.date) meta.append(" ", el("span", "figure", due.date));
            li.append(meta);
            const item = items?.actions[i];
            if (item) li.append(...itemReview(item, true));
            ul.append(li);
          });
          actions.append(ul);
        }
        main.append(actions);
        main.append(reviewListCard(T.figuresAndDeadlines, s.figures_and_deadlines, items?.figures_and_deadlines, "No figures or deadlines mentioned."));
        main.append(reviewListCard(T.openQuestions, s.open_questions, items?.open_questions, "No open questions."));
      }
      const paused = named ? pausesLine(s.details.recording_pauses, opts.timeZone) : null;
      if (paused) main.append(el("p", "muted pk-footnote pk-pauses-line", paused));
      if (s.audio_deleted_at) main.append(el("p", "muted pk-footnote", audioDeletedLine(s.audio_deleted_at, opts.timeZone)));
      const side = el("div", "pk-summary-side");
      side.append(handOverCard(s), transcriptCard(s));
      grid.append(main, side);
      page.append(grid);
    }
    opts.root.replaceChildren(page);
  }

  void (async () => {
    try {
      [summary, detail] = await Promise.all([fetchSummary(opts.meetingId), fetchMeeting(opts.meetingId)]);
      draw();
      if (canReview()) {
        members = (await fetchHandoverOptions(opts.meetingId)).members;
      }
    } catch (err) {
      if (!summary) error = messageOf(err, "Preekie couldn't load these notes. Try again in a few minutes.");
      else if (canReview()) actionError = messageOf(err, "Preekie couldn't load who to hand over to.");
    }
    draw();
  })();
  draw();
  return {
    destroy() {
      destroyed = true;
    },
  };
}
